/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { resolveCoreApiPath } from '../utils/coreAssetUrl';

export {
  resolveCompanyLogoMediaScopeId,
  resolveDmsMediaCallerScopeId,
  resolveSystemMediaCallerScopeId,
} from './dmsMediaScope';

export const QUBEPRO_DMS_FILE_REF_PREFIX = 'dms:FILE:';
/** DMS servisinin iç platform kapsamı; browser isteklerinde doğrudan kullanılmaz. */
export const QUBEPRO_CORE_DMS_COMPANY_SCOPE_ID = '0';

export type DmsMediaUrlOptions = {
  companyScope?: string | number | bigint | null;
  /** CORE-owned avatar/logo; resource scope is fixed server-side and never asserted by the browser. */
  systemMedia?: boolean;
};

export type DmsContentUrlOptions = Omit<DmsMediaUrlOptions, 'systemMedia'> & {
  download?: boolean;
};

export type DmsImagePrefetchDescriptor = {
  ref: string | null | undefined;
  companyScope?: string | number | bigint | null;
  systemMedia?: boolean;
};

export type DmsImagePrefetchInput =
  | string
  | null
  | undefined
  | DmsImagePrefetchDescriptor;

function isDmsImagePrefetchDescriptor(
  input: DmsImagePrefetchInput,
): input is DmsImagePrefetchDescriptor {
  return typeof input === 'object' && input !== null;
}

export function appendDmsMediaQuery(path: string, options?: DmsMediaUrlOptions): string {
  const companyScope = options?.companyScope != null ? String(options.companyScope).trim() : '';
  if (!companyScope) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}companyId=${encodeURIComponent(companyScope)}`;
}

export function isDmsFileRef(stored: string | null | undefined): boolean {
  return typeof stored === 'string' && /^dms:FILE:[1-9]\d*$/.test(stored);
}

export function parseDmsFileId(stored: string | null | undefined): string | null {
  if (!isDmsFileRef(stored)) return null;
  const id = stored!.slice(QUBEPRO_DMS_FILE_REF_PREFIX.length).trim();
  return id || null;
}

/** DMS dosya önizleme — frontend ürün-local `/api/dms/...` der; backend DMS/Core köprüsünü yapar. */
export function getDmsFilePreviewRequestPath(fileId: string, options?: DmsMediaUrlOptions): string {
  return resolveCoreApiPath(appendDmsMediaQuery(`/api/dms/files/${encodeURIComponent(fileId)}/preview`, options));
}

/**
 * Genel dosya içeriğini storage adresini açığa çıkarmadan ürün-local DMS proxy üzerinden açar.
 * `download` yalnız Content-Disposition davranışını değiştirir; yetki ve firma kapsamı değişmez.
 */
export function getDmsFileContentRequestPath(
  fileId: string,
  options: DmsContentUrlOptions = {},
): string {
  let path = appendDmsMediaQuery(`/api/dms/files/${encodeURIComponent(fileId)}/content`, options);
  if (options.download) {
    path += `${path.includes('?') ? '&' : '?'}download=1`;
  }
  return resolveCoreApiPath(path);
}

export function getDmsFileContentStableUrl(
  stored: string | null | undefined,
  options: DmsContentUrlOptions = {},
): string | null {
  if (!stored) return null;
  const value = String(stored).trim();
  if (!value) return null;
  const fileId = parseDmsFileId(value);
  if (fileId) return getDmsFileContentRequestPath(fileId, options);
  return getDmsFileImageStableUrl(value, options);
}

/**
 * STABİL + immutable görsel URL'i (avatar/logo için). fileId değişmedikçe URL sabit
 * kalır → tarayıcı kalıcı cache'ler, presigned URL tarayıcıya hiç sızmaz, "flash" biter.
 * Kalıcı medya yalnız DMS referansı kabul eder. Ref yoksa null.
 */
export function getDmsFileImageStableUrl(
  stored: string | null | undefined,
  options?: DmsMediaUrlOptions,
): string | null {
  if (!stored) return null;
  const s = String(stored).trim();
  if (!s) return null;
  const fileId = parseDmsFileId(s);
  if (fileId) {
    const path = options?.systemMedia
      ? `/api/dms/system-media/files/${encodeURIComponent(fileId)}/image`
      : `/api/dms/files/${encodeURIComponent(fileId)}/image`;
    return resolveCoreApiPath(appendDmsMediaQuery(path, options));
  }

  return null;
}

/**
 * Bootstrap'ta SESSİZ önyükleme: stabil görsel URL'lerini, ilgili alan ekranda
 * görünmeden tarayıcı cache'ine alır. Immutable cache sayesinde bir kez iner,
 * sonraki render'larda anında görünür (geçiş/flash olmaz).
 */
export function prefetchDmsImages(
  refs: DmsImagePrefetchInput[],
  options?: DmsMediaUrlOptions,
): void {
  if (typeof window === 'undefined' || typeof Image === 'undefined') return;
  const seen = new Set<string>();
  for (const input of refs) {
    const isDescriptor = isDmsImagePrefetchDescriptor(input);
    const ref = isDescriptor ? input.ref : input;
    const itemOptions =
      isDescriptor
        ? {
          companyScope: input.companyScope ?? options?.companyScope,
          systemMedia: input.systemMedia ?? options?.systemMedia,
        }
        : options;
    const url = getDmsFileImageStableUrl(ref, itemOptions);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    } catch {
      /* önyükleme best-effort; başarısızlık akışı bozmaz */
    }
  }
}
